import { useMutation, useQueryClient } from "@tanstack/react-query";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { patch } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";

import type { AccountLimits } from "../types";

async function updateAccountLimits(
  workspaceId: string,
  accountId: string,
  limits: AccountLimits,
  abortSignal?: AbortSignal,
) {
  await patch(
    `workspaces/${workspaceId}/accounts/${accountId}/limits`,
    limits,
    abortSignal,
  );
}

export default function useAccountLimitsMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { mutateAsync: setAccountLimits, isPending: isSettingAccountLimits } =
    useMutation({
      mutationFn: ({
        accountId,
        limits,
        abortSignal,
      }: {
        accountId: string;
        limits: AccountLimits;
        abortSignal?: AbortSignal;
      }) => updateAccountLimits(workspaceId, accountId, limits, abortSignal),
      onMutate: ({ accountId, limits: newLimits }) => {
        queryClient.setQueryData(
          getQueryKeys(workspaceId).accountsKeys.limits(accountId),
          newLimits,
        );
      },
    });

  return {
    setAccountLimits,
    isSettingAccountLimits,
  };
}
